import React from "react";
import type { NegotiationBalance } from "../../services/negotiationBalance";

/**
 * What the Átrio board and the bid dialog may ask of the page that owns the
 * Saldo de Negociações on /negociar. The value changes only when
 * `canRecharge` flips, never on a poll, so consuming it does not re-render
 * the memoized board every 15 s.
 */
export type NegotiationBalanceActions = {
  /** Re-read GET /payment/negotiation-balance now. */
  refresh: () => void;
  /**
   * A recharge can open from the auction right now: the page holds a prepaid
   * balance answer that accepts top-ups, and the collaborator may bid. A
   * shortcut is offered only while it is true.
   */
  canRecharge: boolean;
  /**
   * Open the recharge QR modal, prefilled with whole reais when given. It is
   * ignored while `canRecharge` is false, so a click never queues a modal
   * that would pop up later on its own.
   */
  openRecharge: (amountBRL?: number) => void;
  /**
   * The page's last prepaid balance answer, read without re-rendering: null
   * when there is none (no account, rail efi) or the last read failed. Only a
   * coarse local gate for a turbo bid that has no quote of its own.
   */
  prepaidSnapshot: () => NegotiationBalance | null;
  /**
   * Called when the balance moved (a new revision was read, or a recharge
   * modal closed), so open previews can be read again. Returns the unsubscribe.
   */
  subscribe: (listener: () => void) => () => void;
};

/** null outside the Átrio page: the bid UI then shows no balance shortcut. */
export const NegotiationBalanceActionsContext =
  React.createContext<NegotiationBalanceActions | null>(null);

export function useNegotiationBalanceActions(): NegotiationBalanceActions | null {
  return React.useContext(NegotiationBalanceActionsContext);
}
